"use client";

import { useEffect, useRef, useState } from "react";

/** How long a replay frame holds before the next is drawn. */
const FRAME_MS = 100;

/**
 * A replay's clock: it runs while playing and while its element is on
 * screen, a frame every FRAME_MS, and stops at the end. With reduced motion
 * it rests on the end until asked to play. `toggle` pauses, resumes, or
 * replays from the start once ended. Attach `ref` to the player's element.
 * A player mounted before its replay should start (to load what it draws
 * ahead of time) holds its first frame until `armed`.
 */
export function useReplayClock(endsMs: number, reduceMotion: boolean, armed = true) {
  const [atMs, setAtMs] = useState(reduceMotion ? endsMs : 0);
  const [playing, setPlaying] = useState(!reduceMotion);
  const [onScreen, setOnScreen] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const clock = useRef(atMs);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      setOnScreen(entries.some((entry) => entry.isIntersecting));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!armed || !playing || !onScreen) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      if (now - last >= FRAME_MS) {
        clock.current = Math.min(endsMs, clock.current + (now - last));
        last = now;
        setAtMs(clock.current);
        if (clock.current >= endsMs) {
          setPlaying(false);
          return;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [armed, playing, onScreen, endsMs]);

  const ended = atMs >= endsMs;
  const toggle = () => {
    if (ended) {
      clock.current = 0;
      setAtMs(0);
      setPlaying(true);
      return;
    }
    setPlaying((current) => !current);
  };
  return { ref, atMs, playing, ended, toggle };
}
